"use client";

import { projectDataSourceSeed } from "@/lib/project-data-converters/client-cache";
import type {
  AggregatedProjectData,
  FreeformCheckResult,
  FreeformCheckTemplate,
  OutputLanguage,
  ProjectDataSourceDescriptor,
} from "@/types/aicheck";

export const FREEFORM_RESULT_CACHE_PREFIX = "aicheck:freeform-review-result:";
export const FREEFORM_RESULT_CACHE_VERSION = 1;

export interface FreeformReviewResultCacheEntry {
  projectId: number;
  language: OutputLanguage;
  templateId: string;
  templateName: string;
  stage: string;
  promptHash: string;
  prompt: string;
  source?: ProjectDataSourceDescriptor;
  /**
   * @deprecated No longer written. The full project data is redundant with the
   * source cache (and the AI check result cache), so it is no longer persisted
   * here. Retained only so legacy entries / imported bundles still parse.
   */
  projectData?: AggregatedProjectData;
  result: FreeformCheckResult;
  cachedAt: string;
}

interface StoredFreeformReviewResultCacheEntry {
  v: number;
  data: FreeformReviewResultCacheEntry;
}

interface StoredFreeformReviewResultCache {
  key: string;
  entry: FreeformReviewResultCacheEntry;
}

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

function fnv32(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function hashFreeformReviewPrompt(prompt: string): string {
  return fnv32(prompt.trim());
}

export function getFreeformReviewResultCacheKey(params: {
  projectId: number;
  language: OutputLanguage;
  source?: ProjectDataSourceDescriptor;
}): string {
  return [
    FREEFORM_RESULT_CACHE_PREFIX,
    params.language,
    encodeURIComponent(freeformReviewLookupIdentity(params)),
  ].join(":");
}

function freeformReviewSourceIdentity(
  source: ProjectDataSourceDescriptor | null | undefined,
): string | null {
  if (!source) return null;

  const crmProjectId = source?.origin?.crmProjectId;
  if (
    typeof crmProjectId === "number" &&
    Number.isFinite(crmProjectId) &&
    crmProjectId > 0
  ) {
    return `crm:${crmProjectId}`;
  }

  const seed = projectDataSourceSeed(source);
  return seed ? `source:${seed}` : null;
}

function freeformReviewLookupIdentity(params: {
  projectId: number;
  source?: ProjectDataSourceDescriptor | null;
}): string {
  return (
    freeformReviewSourceIdentity(params.source) ?? `project:${params.projectId}`
  );
}

function freeformReviewResultIdentity(
  entry: Pick<
    FreeformReviewResultCacheEntry,
    "projectId" | "language" | "source"
  >,
): string {
  return [entry.language, freeformReviewLookupIdentity(entry)].join(":");
}

function matchesFreeformReviewResultCache(params: {
  entry: FreeformReviewResultCacheEntry;
  projectId: number;
  language: OutputLanguage;
  source?: ProjectDataSourceDescriptor;
}): boolean {
  return (
    params.entry.language === params.language &&
    freeformReviewLookupIdentity(params.entry) ===
      freeformReviewLookupIdentity(params)
  );
}

export function readFreeformReviewResultCache(params: {
  projectId?: number;
  language: OutputLanguage;
  source?: ProjectDataSourceDescriptor;
}): FreeformReviewResultCacheEntry | null {
  if (!canUseLocalStorage() || !params.projectId) {
    return null;
  }
  const projectId = params.projectId;
  try {
    const raw = localStorage.getItem(
      getFreeformReviewResultCacheKey({
        projectId,
        language: params.language,
        source: params.source,
      }),
    );
    const parsed = parseFreeformReviewResultCache(raw);
    if (
      parsed &&
      matchesFreeformReviewResultCache({
        entry: parsed,
        projectId,
        language: params.language,
        source: params.source,
      })
    ) {
      return parsed;
    }

    return (
      listStoredFreeformReviewResultCaches()
        .map((stored) => stored.entry)
        .filter((entry) =>
          matchesFreeformReviewResultCache({
            entry,
            projectId,
            language: params.language,
            source: params.source,
          }),
        )
        .sort((a, b) => b.cachedAt.localeCompare(a.cachedAt))[0] ?? null
    );
  } catch {
    return null;
  }
}

function parseFreeformReviewResultCache(
  raw: string | null,
): FreeformReviewResultCacheEntry | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredFreeformReviewResultCacheEntry;
    if (parsed?.v !== FREEFORM_RESULT_CACHE_VERSION || !parsed.data?.result) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function listStoredFreeformReviewResultCaches(): StoredFreeformReviewResultCache[] {
  const entries: StoredFreeformReviewResultCache[] = [];
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(FREEFORM_RESULT_CACHE_PREFIX)) continue;
      const entry = parseFreeformReviewResultCache(localStorage.getItem(key));
      if (entry) entries.push({ key, entry });
    }
  } catch {
    return entries;
  }
  return entries;
}

export function listFreeformReviewResultCaches(): FreeformReviewResultCacheEntry[] {
  if (!canUseLocalStorage()) return [];
  const deduped = new Map<string, FreeformReviewResultCacheEntry>();
  for (const { entry } of listStoredFreeformReviewResultCaches()) {
    const identity = freeformReviewResultIdentity(entry);
    const current = deduped.get(identity);
    if (!current || entry.cachedAt.localeCompare(current.cachedAt) > 0) {
      deduped.set(identity, entry);
    }
  }
  return [...deduped.values()].sort((a, b) =>
    b.cachedAt.localeCompare(a.cachedAt),
  );
}

export function readLatestFreeformReviewResultCache(params: {
  projectId: number;
  language?: OutputLanguage;
  source?: ProjectDataSourceDescriptor;
}): FreeformReviewResultCacheEntry | null {
  const requestedIdentity = freeformReviewLookupIdentity(params);
  return (
    listFreeformReviewResultCaches().find(
      (entry) =>
        (!params.language || entry.language === params.language) &&
        freeformReviewLookupIdentity(entry) === requestedIdentity,
    ) ?? null
  );
}

function removeDuplicateFreeformReviewResultCaches(
  entry: FreeformReviewResultCacheEntry,
  keepKey: string,
): void {
  const identity = freeformReviewResultIdentity(entry);
  const keysToRemove = listStoredFreeformReviewResultCaches()
    .filter(
      (stored) =>
        stored.key !== keepKey &&
        freeformReviewResultIdentity(stored.entry) === identity,
    )
    .map((stored) => stored.key);

  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
}

function isQuotaExceededError(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false;
  return (
    error.name === "QuotaExceededError" ||
    error.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    // Legacy numeric codes (Chrome: 22, Firefox: 1014).
    error.code === 22 ||
    error.code === 1014
  );
}

/**
 * Writes to localStorage, returning `true` on success and `false` when the
 * write fails because the quota is exceeded. Any other error is rethrown.
 */
function trySetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (isQuotaExceededError(error)) return false;
    throw error;
  }
}

/**
 * Frees space by removing the oldest freeform review result cache entry, other
 * than `excludeKey`. Returns `true` when an entry was removed.
 */
function evictOldestFreeformReviewResultCache(excludeKey: string): boolean {
  const oldest = listStoredFreeformReviewResultCaches()
    .filter((stored) => stored.key !== excludeKey)
    .sort((a, b) => a.entry.cachedAt.localeCompare(b.entry.cachedAt))[0];
  if (!oldest) return false;
  try {
    localStorage.removeItem(oldest.key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Persists `payload` under `storageKey`, evicting the oldest sibling entries to
 * reclaim quota when needed. Returns `true` once the value is stored.
 */
function storeWithEviction(storageKey: string, payload: string): boolean {
  if (trySetItem(storageKey, payload)) return true;
  while (evictOldestFreeformReviewResultCache(storageKey)) {
    if (trySetItem(storageKey, payload)) return true;
  }
  return false;
}

/**
 * Returns a copy of `entry` without the legacy `projectData` field. The full
 * project data is redundant with the source cache, so it is stripped before
 * persisting or sharing to keep storage and bundles small.
 */
export function omitFreeformReviewProjectData(
  entry: FreeformReviewResultCacheEntry,
): FreeformReviewResultCacheEntry {
  if (entry.projectData === undefined) return entry;
  const next = { ...entry };
  delete next.projectData;
  return next;
}

export function writeFreeformReviewResultCache(params: {
  projectId?: number;
  language: OutputLanguage;
  template: FreeformCheckTemplate;
  prompt: string;
  source?: ProjectDataSourceDescriptor;
  result: FreeformCheckResult;
}): FreeformReviewResultCacheEntry | null {
  if (!canUseLocalStorage() || !params.projectId) return null;
  const prompt = params.prompt.trim();
  if (!prompt) return null;
  // `projectData` is intentionally not stored — it is redundant with the source
  // cache and is the main driver of the localStorage quota being exceeded.
  const entry: FreeformReviewResultCacheEntry = {
    projectId: params.projectId,
    language: params.language,
    templateId: params.template.id,
    templateName: params.template.stage,
    stage: params.template.stage,
    promptHash: hashFreeformReviewPrompt(prompt),
    prompt,
    source: params.source,
    result: params.result,
    cachedAt: new Date().toISOString(),
  };
  const storageKey = getFreeformReviewResultCacheKey({
    projectId: entry.projectId,
    language: entry.language,
    source: entry.source,
  });

  try {
    const saved = storeWithEviction(
      storageKey,
      JSON.stringify({
        v: FREEFORM_RESULT_CACHE_VERSION,
        data: entry,
      } satisfies StoredFreeformReviewResultCacheEntry),
    );
    if (!saved) {
      console.warn(
        "[freeform-review-cache] Failed to save result: storage quota exceeded",
      );
      return null;
    }
    removeDuplicateFreeformReviewResultCaches(entry, storageKey);
    return entry;
  } catch (error) {
    console.warn("[freeform-review-cache] Failed to save result", error);
    return null;
  }
}
